/**
 * Hex color math: parse, lighten/darken, and derive state colors from a token.
 *
 * `toHoverColor` and `toPressColor` lighten a base color when it is dark and
 * darken it when it is light, so a derived state color keeps its contrast under
 * any theme. The `translucent*` variants instead pick a constant white-or-black
 * overlay from the brightness of the surface they sit on — for controls layered
 * over an image, where the color underneath isn't known.
 *
 * Lives beside the tokens rather than in the client so `tokensToCssVars` can
 * derive variables without reaching across the package boundary.
 */

// PSI's default white surface, for callers with no active theme to read. Inside
// a component, pass `useTheme().colors.colorSurfacePrimary` instead.
const DEFAULT_SURFACE_COLOR = '#FFFFFF';

const DARK_SURFACE_BRIGHTNESS = 0.2;

/** @public */
export function parseHexColor(hexColor: string): { r: number; g: number; b: number } {
    return {
        r: parseInt(hexColor.slice(1, 3), 16),
        g: parseInt(hexColor.slice(3, 5), 16),
        b: parseInt(hexColor.slice(5, 7), 16),
    };
}

/** @public */
export function toHexColor(r: number, g: number, b: number): string {
    const toHex = (c: number) => Math.round(c).toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function colorBrightness(hexColor: string): number {
    const { r, g, b } = parseHexColor(hexColor);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darkenColor(hexColor: string, percentage: number): string {
    const { r, g, b } = parseHexColor(hexColor);
    return toHexColor(r * (1 - percentage), g * (1 - percentage), b * (1 - percentage));
}

function lightenColor(hexColor: string, percentage: number): string {
    const { r, g, b } = parseHexColor(hexColor);
    return toHexColor(
        r * (1 - percentage) + 255 * percentage,
        g * (1 - percentage) + 255 * percentage,
        b * (1 - percentage) + 255 * percentage,
    );
}

/** @public */
export function toHoverColor(hexColor: string, factor = 0.2): string {
    return colorBrightness(hexColor) <= DARK_SURFACE_BRIGHTNESS ? lightenColor(hexColor, factor) : darkenColor(hexColor, factor);
}

/** @public */
export function toPressColor(hexColor: string, factor = 0.4): string {
    return colorBrightness(hexColor) <= DARK_SURFACE_BRIGHTNESS ? lightenColor(hexColor, factor) : darkenColor(hexColor, factor);
}

/**
 * A translucent hover overlay — white or black at 20% opacity — chosen from the
 * brightness of the surface it covers.
 * @public
 */
export function translucentHoverColor(surfaceColor = DEFAULT_SURFACE_COLOR): string {
    return colorBrightness(surfaceColor) <= DARK_SURFACE_BRIGHTNESS ? '#FFFFFF33' : '#00000033';
}

/**
 * A translucent press overlay — white or black at 40% opacity — chosen from the
 * brightness of the surface it covers.
 * @public
 */
export function translucentPressColor(surfaceColor = DEFAULT_SURFACE_COLOR): string {
    return colorBrightness(surfaceColor) <= DARK_SURFACE_BRIGHTNESS ? '#FFFFFF66' : '#00000066';
}
